import React from "react";
import { useTranslation } from "react-i18next";
import { ColumnDef, Column, Row } from "@tanstack/react-table";
import { Button } from "@/components/ui/Button";
import { Download } from "lucide-react";
import { DataTable } from "@/components/DataTable";
import { formatDate } from "@/lib/formatDate";
import { DataManagementFile } from "@/hooks/useDataManagementFiles";

interface UseDataManagementColumnsProps {
  onDownload: (file: DataManagementFile) => void;
}

export const useDataManagementColumns = ({
  onDownload,
}: UseDataManagementColumnsProps) => {
  const { t, i18n } = useTranslation();

  const columns: ColumnDef<DataManagementFile>[] = [
    {
      id: "createdAt",
      accessorKey: "createdAt",
      enableSorting: true,
      enableColumnFilter: false,
      meta: {
        label: t("dataManagement.table.uploadDate"),
      },
      header: ({ column }: { column: Column<DataManagementFile> }) => (
        <DataTable.THeaderCellSorting column={column}>
          {t("dataManagement.table.uploadDate")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<DataManagementFile> }) => {
        const date = row.getValue("createdAt") as number;
        return (
          <DataTable.TBodyCell>
            {date
              ? formatDate(
                  new Date(date * 1000),
                  t("dataManagement.table.dateFormat"),
                  { locale: i18n.language as "en" },
                )
              : ""}
          </DataTable.TBodyCell>
        );
      },
    },
    {
      id: "name",
      accessorKey: "name",
      enableSorting: true,
      enableColumnFilter: false,
      meta: {
        label: t("dataManagement.table.fileName"),
      },
      header: () => (
        <DataTable.THeaderCell>
          {t("dataManagement.table.fileName")}
        </DataTable.THeaderCell>
      ),
      cell: ({ row }: { row: Row<DataManagementFile> }) => {
        const name = row.getValue("name") as string;
        return <DataTable.TBodyCell>{name}</DataTable.TBodyCell>;
      },
    },
    {
      id: "download",
      header: "",
      cell: ({ row }: { row: Row<DataManagementFile> }) => (
        <DataTable.TBodyCell isLastCell={true}>
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDownload(row.original)}
              disabled={!row.original.url}
            >
              <Download className="mr-2 h-4 w-4" />
              {t("dataManagement.table.download")}
            </Button>
          </div>
        </DataTable.TBodyCell>
      ),
      enableSorting: false,
      enableColumnFilter: false,
    },
  ];

  return columns;
};
